import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"
import { debugLog } from "@/utils/debugLog"
import {
  ColorPicker,
  ColorSwatch,
  Popover,
  Group,
  ActionIcon,
  Stack,
} from "@mantine/core"
import { IconCheck, IconTrash, IconX } from "@tabler/icons-react"

interface ColorPickerButtonProps {
  color?: string
  onConfirm?: (color: string | undefined) => void
}

// 8 default color swatches
const defaultSwatches = [
  "#FFFFFF",
  "#000000",
  "#FF0000",
  "#00FF00",
  "#0000FF",
  "#FFFF00",
  "#00FFFF",
  "#FF00FF",
]

export function ColorPickerButton({
  color,
  onConfirm,
}: ColorPickerButtonProps) {
  const { colors: lastUsedColors, addColor } = useLastUsedColors()
  const [currentColor, setCurrentColor] = React.useState<string>(
    color ?? "transparent"
  )
  const [popoverOpened, setPopoverOpened] = React.useState(false)
  const handleChange = (newColor: string) => {
    debugLog("Color changed to:", newColor)
    setCurrentColor(newColor)
  }

  const handleConfirm = () => {
    const confirmedColor =
      currentColor === "transparent" ? undefined : currentColor
    debugLog("Confirmed color:", confirmedColor)
    confirmedColor && addColor(confirmedColor)
    onConfirm?.(confirmedColor)
    setPopoverOpened(false)
  }
  const handleClose = () => {
    setPopoverOpened(false)
  }
  const handleDelete = () => {
    setCurrentColor("transparent")
  }
  return (
    <Popover opened={popoverOpened} onClose={handleClose}>
      <Popover.Target>
        <ColorSwatch
          size="36"
          radius="xs"
          color={color ?? "transparent"}
          onClick={() => setPopoverOpened((o) => !o)}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <Stack>
          <Group justify="flex-end">
            <ActionIcon variant="default" onClick={handleClose}>
              <IconX />
            </ActionIcon>
          </Group>
          <ColorPicker
            value={currentColor}
            swatchesPerRow={8}
            swatches={[...defaultSwatches, ...lastUsedColors]}
            onChange={handleChange}
          />
          <Group justify="space-between">
            <Group>
              <ColorSwatch size="36" radius="xs" color={currentColor} />
              <ActionIcon variant="subtle" color="red" onClick={handleDelete}>
                <IconTrash />
              </ActionIcon>
            </Group>
            <Group>
              <ActionIcon onClick={handleConfirm}>
                <IconCheck />
              </ActionIcon>
            </Group>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}

// 8 last used colors stored in local storage
interface useLastUsedColorsReturnValue {
  colors: string[]
  addColor: (color: string) => void
}
function useLastUsedColors(): useLastUsedColorsReturnValue {
  const [storedColors, setStoredColors] = useUserLocalStorage<string[]>({
    key: "last-used-colors",
    defaultValue: [],
  })
  const addColor = (color: string) => {
    setStoredColors((prevColors) => {
      const newColors = [color, ...prevColors.filter((c) => c !== color)]
      return newColors.slice(0, 8) // keep only the last 8 colors
    })
  }

  return {
    colors: storedColors,
    addColor,
  }
}
